// Session renewal that survives deploys and busy pages (stay signed in,
// CEO 2026-10-22: "we get signed out daily").
//
// The 15-minute access token is renewed from the httpOnly refresh cookie.
// Before this module, ANY failed renewal signed the person out:
//   - a deploy (Railway swapping containers → 502/503 or a dropped
//     connection for a few seconds) or a database blip looked exactly like
//     "your session is over";
//   - a page that fired several requests after the access token ran out
//     (coming back to the tab refetches everything at once) sent several
//     renewals with the same cookie; the first rotated it and the rest were
//     refused → signed out. Two tabs waking up together did the same.
//
// Now:
//   - ONE renewal at a time per tab (single flight), and one at a time
//     across tabs (Web Locks), so a cookie is never presented twice;
//   - network errors, 5xx, 429 and anything that isn't a clean answer are
//     retried with backoff for about a minute (a deploy's swap window);
//   - only a definitive 401 from the server means "signed out";
//   - still failing after the retries = "unavailable": the caller shows an
//     error (on page load: "Can't reach … — retrying…" with a Retry button)
//     but KEEPS the session (the cookie is intact; the server no longer
//     deletes it on a failed renewal).
//
// Pure module (no React) so db/qa-stay-signed-in.ts executes it offline.
// The same file lives in apps/web, apps/hub and apps/client-portal; the
// gate checks the three copies are identical.

export type RefreshOutcome<U = unknown> =
  | { kind: "ok"; accessToken: string; user: U | null }
  | { kind: "signed_out" }
  | {
      kind: "unavailable";
      /** The server's own words, when it sent any (e.g. "two-step sign-in is
       *  misconfigured — contact admin"). */
      message?: string;
      /** The server answered on purpose and retrying won't change it (e.g.
       *  mfa_misconfigured) — stop retrying and show the message. */
      definitive?: boolean;
    };

/** Server error codes that won't go away by retrying a renewal. */
export const DEFINITIVE_REFRESH_ERRORS: readonly string[] = ["mfa_misconfigured"];

/** Waits between attempts: ~60 s in all, the length of a deploy swap.
 *  Each wait is jittered ±25% so many tabs don't retry in lock-step. */
export const REFRESH_RETRY_DELAYS_MS: readonly number[] = [
  500, 1_000, 2_000, 3_000, 5_000, 8_000, 10_000, 15_000, 15_000,
];

/** One wait with ±25% jitter (`random` in [0, 1)). */
export function withJitter(ms: number, random: () => number = Math.random): number {
  return Math.round(ms * (0.75 + 0.5 * random()));
}

/** What one HTTP answer to the renewal means. Only 401 signs out. */
export function classifyRefreshStatus(status: number): "ok" | "signed_out" | "retry" {
  if (status >= 200 && status < 300) return "ok";
  if (status === 401) return "signed_out";
  return "retry";
}

type LockFn = <T>(name: string, fn: () => Promise<T>) => Promise<T>;

export interface SessionRefresherDeps {
  /** "/auth/refresh" (staff) or "/portal/auth/refresh" (client portal). */
  url: string;
  fetch?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  /** Cross-tab mutual exclusion. Default: navigator.locks when present. */
  lock?: LockFn | null;
  delaysMs?: readonly number[];
  random?: () => number;
}

/** refresh(), plus retryNow(): skip the current wait (the Retry button). */
export type SessionRefresher<U> = (() => Promise<RefreshOutcome<U>>) & { retryNow: () => void };

const LOCK_WAIT_MS = 90_000;

/** navigator.locks.request, giving up the wait (and running anyway) after
 *  LOCK_WAIT_MS so a stuck tab can never freeze the others. */
function browserLock(): LockFn | null {
  const nav = (globalThis as { navigator?: { locks?: { request?: unknown } } }).navigator;
  const locks = nav?.locks as
    | { request: (name: string, opts: { signal?: AbortSignal }, fn: () => Promise<unknown>) => Promise<unknown> }
    | undefined;
  if (!locks || typeof locks.request !== "function") return null;
  return async <T>(name: string, fn: () => Promise<T>): Promise<T> => {
    const ctl = typeof AbortController === "function" ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), LOCK_WAIT_MS) : null;
    let started = false;
    try {
      return (await locks.request(name, ctl ? { signal: ctl.signal } : {}, () => {
        started = true;
        if (timer) clearTimeout(timer);
        return fn();
      })) as T;
    } catch (e) {
      if (!started) return fn(); // waited too long for the lock → go anyway
      throw e;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };
}

/**
 * Returns `refresh()`: renew the session, single-flight within the tab,
 * serialized across tabs, retried (with jitter) through deploys.
 */
export function makeSessionRefresher<U = unknown>(
  deps: SessionRefresherDeps,
): SessionRefresher<U> {
  const doFetch = deps.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const lock = deps.lock === undefined ? browserLock() : deps.lock;
  const delays = deps.delaysMs ?? REFRESH_RETRY_DELAYS_MS;
  const random = deps.random ?? Math.random;
  let wake: (() => void) | null = null;
  let inFlight: Promise<RefreshOutcome<U>> | null = null;

  /** The server's last words during this round (shown if we give up). */
  let lastMessage: string | undefined;

  async function attemptOnce(): Promise<RefreshOutcome<U> | "retry"> {
    let res: Response;
    try {
      res = await doFetch(deps.url, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
    } catch {
      return "retry"; // network error / connection dropped mid-deploy
    }
    const verdict = classifyRefreshStatus(res.status);
    if (verdict === "signed_out") return { kind: "signed_out" };
    if (verdict === "retry") {
      try {
        const body = (await res.json()) as { error?: unknown; message?: unknown };
        const code = typeof body?.error === "string" ? body.error : undefined;
        const text = typeof body?.message === "string" ? body.message : code;
        if (text) lastMessage = text;
        if (code && DEFINITIVE_REFRESH_ERRORS.includes(code)) {
          return { kind: "unavailable", message: text, definitive: true };
        }
      } catch {
        // not JSON (an edge error page) — nothing to say
      }
      return "retry";
    }
    try {
      const body = (await res.json()) as { access_token?: unknown; user?: U };
      if (typeof body?.access_token === "string" && body.access_token !== "") {
        return { kind: "ok", accessToken: body.access_token, user: body.user ?? null };
      }
    } catch {
      // not JSON (e.g. an HTML error page from the edge) → retry
    }
    return "retry";
  }

  async function run(): Promise<RefreshOutcome<U>> {
    lastMessage = undefined;
    for (let i = 0; ; i++) {
      const r = await attemptOnce();
      if (r !== "retry") return r;
      if (i >= delays.length) {
        return lastMessage ? { kind: "unavailable", message: lastMessage } : { kind: "unavailable" };
      }
      await Promise.race([
        sleep(withJitter(delays[i]!, random)),
        new Promise<void>((r) => {
          wake = r;
        }),
      ]);
      wake = null;
    }
  }

  const refresh = (): Promise<RefreshOutcome<U>> => {
    if (!inFlight) {
      const go = lock ? lock(`other-session-refresh:${deps.url}`, run) : run();
      inFlight = go.finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };
  return Object.assign(refresh, {
    retryNow: (): void => {
      const w = wake as (() => void) | null;
      if (w) w();
    },
  });
}
